import { useContext, useEffect, useMemo, useState } from 'react';
import { fetchBankList } from '../../pages/Wallets/redux/slice';
import { useAppDispatch, useAppSelector } from '../hooks';
import * as Sentry from "@sentry/react";
import { NetworkErrorAlertContext } from '../../context/NetworkErrorAlert';

type Bank = {
    code: string;
    name: string;
}

type BankOption = {
    label: string;
    value: string;
    originalCode: string;
}

type UseBankOptionsProps = {
    country: string;
    businessId?: string;
    type?: "mobile-money" | undefined
}

export const useBankList = ({ country, businessId, type = undefined }: UseBankOptionsProps) => {
    const dispatch = useAppDispatch(); 

    const { keys } = useAppSelector((state) => state.settings);
    const publicKey = keys?.data?.publicKey;

    const { onShowAlert } = useContext(NetworkErrorAlertContext);

    const [banks, setBanks] = useState<Bank[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchBanks = async () => {
            setLoading(true);
            setError(null);
            try {
                const action = await dispatch(
                    fetchBankList({
                        xAuthToken: publicKey,
                        country: country,
                        type: type,
                    }),
                );

                if (fetchBankList.fulfilled.match(action)) {
                    setBanks(action.payload.data);
                } else if (fetchBankList.rejected.match(action)) {
                    setError(action.error.message || 'Failed to fetch banks');
                }
            } catch (err) {
                setError('An unexpected error occurred while fetching banks.');
                onShowAlert("", err?.response?.data?.error);
                Sentry.captureException(err);
            } finally {
                setLoading(false);
            }
        };

        fetchBanks();
    }, [dispatch, publicKey, country, businessId]); 

    const selectBanks = useMemo<BankOption[]>(() => {
        if (!banks || !Array.isArray(banks) || banks.length === 0) {
            return [];
        }

        const seenCodes = new Set<string>();
        
        return banks
            .filter((bank) => {
                if (seenCodes.has(bank.code)) {
                    return false;
                }
                seenCodes.add(bank.code);
                return true;
            })
            .map((bank, index) => ({
                label: bank.name,
                value: `${bank.code}_${index}`,
                originalCode: bank.code,
            }));
    }, [banks]); 

    return { selectBanks, banks, loading, error };
};